import React from "react";
import styles from "./KPICard.module.css";
import { KPIMetric } from "../types";

interface KPICardProps {
  title: string;
  metric: KPIMetric;
  icon?: React.ReactNode;
  currency?: boolean;
  timeUnit?: boolean;
  invertTrend?: boolean; // When true, lower values are better (e.g., Avg Order Time)
  infoTooltip?: string; // Optional tooltip text to show on info icon hover
}

const KPICard: React.FC<KPICardProps> = ({
  title,
  metric,
  icon,
  currency,
  timeUnit,
  invertTrend = false,
  infoTooltip,
}) => {
  // For invertTrend (e.g., Avg Order Time), lower is better:
  // - Negative change (time decreased) = good = positive trend (green)
  // - Positive change (time increased) = bad = negative trend (red)
  const isPositive = invertTrend ? metric.change <= 0 : metric.change >= 0;

  // For Avg Order Time, swap arrows: green shows trend-down, red shows trend-up
  const arrowIcon = invertTrend
    ? isPositive
      ? "/trend-down-01.svg" // Green with down arrow (time decreased = good)
      : "/trend-up-01.svg" // Red with up arrow (time increased = bad)
    : isPositive
    ? "/trend-up-01.svg" // Normal: green with up arrow
    : "/trend-down-01.svg"; // Normal: red with down arrow

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <div className={styles.titleWrapper}>
          <span className={styles.title}>{title}</span>
          {infoTooltip && (
            <div className={styles.infoIconWrapper}>
              <img
                src="/info-circle.svg"
                alt="info"
                className={styles.infoIcon}
              />
              <div className={styles.infoTooltip}>{infoTooltip}</div>
            </div>
          )}
        </div>
        {icon && <div className={styles.icon}>{icon}</div>}
      </div>
      <div className={styles.value}>
        <span className={styles.number}>{metric.current.toLocaleString()}</span>
        {currency && <span className={styles.currency}> EGP</span>}
        {timeUnit && <span className={styles.currency}> Min</span>}
      </div>
      {metric.label && <div className={styles.label}>{metric.label}</div>}
      <div className={styles.comparison}>
        <div className={styles.changeContainer}>
          <span
            className={`${styles.change} ${!isPositive ? styles.negative : ""}`}
          >
            <span>
              {metric.previous.toLocaleString()}
              {currency && " EGP"}
              {timeUnit && " Min"}
            </span>
            <img
              src={arrowIcon}
              alt={isPositive ? "trend up" : "trend down"}
              className={`${styles.trendIcon} ${
                invertTrend && !isPositive ? styles.trendIconRed : ""
              } ${invertTrend && isPositive ? styles.trendIconGreen : ""}`}
            />
            {Math.abs(metric.change).toFixed(1)}%
          </span>
        </div>
      </div>
    </div>
  );
};

export default KPICard;
